import React from "react";
import { FileRejection, FileWithPath } from "react-dropzone";
import { DateTime } from "luxon";
import { Box, Typography, useTheme } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  UploadRounded as UploadIcon,
  CloseRounded as CloseIcon,
} from "@mui/icons-material";
import { ApiErrorResponse } from "../../api/types/base";
import PatientAPI from "../../api/endpoints/patient";
import { showErrorMessage } from "../../redux/utils";
import { DialogCloseHandler } from "../../types/dialog";
import { displayDropzoneErrors } from "../../utils/files";
import { Toast } from "../../components/Toast";
import FileDropzone from "../../components/FileDropzone";
import BaseDialog from "../../components/dialogs/BaseDialog";
import IconButton from "../../components/IconButton";

const DialogUploadPhoto = ({
  userId,
  open,
  onClose,
}: {
  userId?: string,
  open: boolean,
  onClose?: DialogCloseHandler,
}) => {
  const theme = useTheme();

  const [files, setFiles] = React.useState<FileWithPath[]>([]);
  const [timestamp, setTimestamp] = React.useState<DateTime | null>(null);

  const handleFilesChanged = (acceptedFiles: readonly FileWithPath[], rejectedFiles: readonly FileRejection[]) => {
    displayDropzoneErrors(rejectedFiles);
    setFiles([...acceptedFiles]);
  }

  const handleConfirm = () => {
    if (userId && files.length > 0 && timestamp) {
      PatientAPI.uploadImage({
        id: userId,
        image: files[0],
        timestamp,
      })
      .then(() => {
        Toast.success({ title: 'Uploaded user sample image' });
        onClose?.({}, 'closeClick');
      })
      .catch((error: ApiErrorResponse) => {
        showErrorMessage(error, {
          title: 'Failed to upload sample image',
          message: error.type === 'errorResponse' ? error.code?.description : undefined,
        });
      });
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      confirmText='Upload'
      confirmIcon={<UploadIcon/>}
      confirmDisabled={files.length === 0 || !timestamp}
      onConfirm={handleConfirm}
      containerSx={{ flexDirection: 'row' }}
    >
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '500px', mr: 3 }}>
          <Typography>Manually upload image for user {userId}</Typography>

          <Typography variant='caption' sx={{ color: theme.palette.text.secondary, mt: 1 }}>Step 1</Typography>
          <Typography>Specify when the photo was taken</Typography>
          <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>The image metadata may give an exact timestamp. If not, just use 08:00</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, my: 1 }}>
            <DateTimePicker
              label='Date & Time'
              value={timestamp}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={val => setTimestamp(val)}
              slotProps={{ digitalClockSectionItem: { sx: { borderRadius: 100 } } }}
            />
            <IconButton disabled={!timestamp} onClick={() => setTimestamp(null)}><CloseIcon/></IconButton>
          </Box>
          
          <Typography variant='caption' sx={{ color: theme.palette.error.main }}>CAUTION: Double-check photo and timestamp before uploading!</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
          <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>Step 2</Typography>
          <Typography>Upload sample photo</Typography>

          <FileDropzone
            helperText='Drag & drop photo here, or click to select file'
            fileTypes={{
              // 'image/png':  ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
            }}
            onFilesChanged={handleFilesChanged}
            sx={{ mt: 2, flex: 0 }}
          />
        </Box>
      </>
    </BaseDialog>
  );
}

export default DialogUploadPhoto;